import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { WarningAmber as WarningIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { programsApi, type ProgramStatistics } from '@/api/programs'
import { getErrorMessage } from '@/utils/error'

/**
 * Вкладка «Статистика» шаблона программы.
 *
 * Отвечает на вопрос, которого не видно из одной группы: «в теме 7 буксуют
 * все» — значит проблема не в группе, а в самой теме шаблона.
 */
export function ProgramStatisticsTab({ programId }: { programId: number }) {
  const { t } = useTranslation()

  const [stats, setStats] = useState<ProgramStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      return await programsApi.statistics(programId)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))

      return null
    } finally {
      setLoading(false)
    }
  }, [programId, t])

  useEffect(() => {
    let cancelled = false

    void load().then((result) => {
      if (cancelled || result === null) return
      setStats(result)
    })

    return () => {
      cancelled = true
    }
  }, [load])

  if (loading) {
    return <Skeleton variant="rounded" height={280} />
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (stats === null) {
    return null
  }

  if (stats.assignments_total === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography color="text.secondary">{t('programs.stats.notAssigned')}</Typography>
      </Card>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('programs.stats.summary.assignments')}
              </Typography>
              <Typography variant="h6">
                {stats.assignments_active}/{stats.assignments_total}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('programs.stats.summary.average')}
              </Typography>
              <Typography variant="h6">
                {stats.average_percent === null ? '—' : `${Math.round(stats.average_percent)}%`}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('programs.stats.summary.graded')}
              </Typography>
              <Typography variant="h6">{stats.graded_count}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('programs.stats.columns.theme')}</TableCell>
              <TableCell align="right">{t('programs.stats.columns.assignments')}</TableCell>
              <TableCell align="right">{t('programs.stats.columns.average')}</TableCell>
              <TableCell align="right">{t('programs.stats.columns.works')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.themes.map((theme, index) => (
              <TableRow key={`${theme.source_theme_id ?? 'own'}-${index}`} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{theme.title}</Typography>
                    {theme.is_pit && (
                      <Tooltip title={t('programs.stats.pitHint', { threshold: Math.round(stats.pit_threshold) })}>
                        <WarningIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">{theme.assignments_count}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={theme.is_pit ? 'warning.main' : 'text.primary'}>
                    {theme.average_percent === null ? '—' : `${Math.round(theme.average_percent)}%`}
                  </Typography>
                </TableCell>
                <TableCell align="right">{theme.graded_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
